import { notify } from "../feedback.ts";
import { KeyboardIcon } from "@phosphor-icons/react";
import { Popover } from "@base-ui/react/popover";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Outlet, useNavigate, useParams } from "@tanstack/react-router";
import type * as C from "@zooi/contracts";
import { Fragment, type KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { q } from "../api.ts";
import { number } from "../format.ts";
import { INBOX_GROUP_LABELS, INBOX_LABELS as I } from "../labels.ts";
import { groupInbox } from "../inbox-groups.ts";
import { GroupHeader, ListRow } from "../ui/list.tsx";
import { InboxItemIcon } from "../ui/inbox-icon.tsx";
import { agentEngine, Executor } from "../ui/props.tsx";
import { Skeleton } from "../ui/skeleton.tsx";
import { ProductLabel } from "../ui/product-label.tsx";
import { hrefOf, metaOf, titleOf } from "../inbox-items.ts";
import { useInboxSeen } from "../inbox-seen.ts";
import { useAttention } from "../attention.ts";
import { ErrorText, Kbd, Quiet, TopBar } from "../ui.tsx";
import { type Intent, InboxContext, narrow } from "./inbox/shared.tsx";
export { WorkOrderReview } from "./inbox/WorkOrderReview.tsx";
export { PostReview } from "./inbox/PostReview.tsx";
export { ReadingReview } from "./inbox/ReadingReview.tsx";
export { OutreachReview } from "./inbox/OutreachReview.tsx";
export { ReplyQueueReview } from "./inbox/ReplyQueueReview.tsx";
export { NoticeReview } from "./inbox/NoticeReview.tsx";
export { FactReview } from "./inbox/FactReview.tsx";
export { RuleReview } from "./inbox/RuleReview.tsx";
export { DataSetupReview } from "./inbox/DataSetupReview.tsx";

// The inbox (docs/design-system.md §0.3, §0.4): what waits for a person's
// judgement across the workspace, in a dense list with the review beside it.
// Single-key shortcuts act only while the list has focus; A and X open the
// review first, and the person submits from there.

/** The agent that submitted a plan, when zooi knows its logo (docs/design-system.md §0.7). */
function submitterOf(item: C.InboxItem) {
  const engine = item.kind === "plan" ? agentEngine(item.facts.engine) : undefined;
  return engine && <Executor engine={engine} name={I.engine[engine]} />;
}
/** Plans, work orders, posts, outreach, readings and an agent's data setup have a primary action the person takes here. */
const decidable = (item: C.InboxItem | undefined) => item?.kind === "plan" || item?.kind === "work_order" || item?.kind === "post" || item?.kind === "outreach" || item?.kind === "reply_queue" || item?.kind === "reading" || item?.kind === "campaign_reading"
  || (item?.kind === "data_setup" && item.reason.code !== "data_setup_recommended");

/** Keep shortcut help available without making every visit start with instructions. */
function InboxShortcuts({ item, opened }: { item: C.InboxItem | undefined; opened: boolean }) {
  return <Popover.Root>
    <Popover.Trigger className="z-btn ghost sm"><KeyboardIcon size={16} aria-hidden />{I.shortcuts}</Popover.Trigger>
    <Popover.Portal><Popover.Positioner sideOffset={8} align="end">
      <Popover.Popup className="z-layer z-shortcuts">
        <Popover.Title className="z-shortcuts-title">{I.keyboardShortcuts}</Popover.Title>
        <dl>
          <div><dt><Kbd>J</Kbd> <Kbd>K</Kbd></dt><dd>{I.keyMove}</dd></div>
          <div><dt><Kbd>Enter</Kbd></dt><dd>{I.keyOpen}</dd></div>
          {decidable(item) && <div><dt><Kbd>A</Kbd></dt><dd>{I.keyReview}</dd></div>}
          {item?.kind === "plan" && <div><dt><Kbd>X</Kbd></dt><dd>{I.keyReject}</dd></div>}
          {opened && <div><dt><Kbd>Esc</Kbd></dt><dd>{I.close}</dd></div>}
        </dl>
      </Popover.Popup>
    </Popover.Positioner></Popover.Portal>
  </Popover.Root>;
}

export function InboxPage() {
  const navigate = useNavigate();
  const client = useQueryClient();
  const params = useParams({ strict: false }) as { planId?: string; orderId?: string; noticeKey?: string; postId?: string; outreachId?: string; replyQueueId?: string; readingId?: string; campaignId?: string; factProductId?: string; ruleProductId?: string; dataProductId?: string };
  const openKey = params.planId ? `plan:${params.planId}` : params.orderId ? `work_order:${params.orderId}` : params.postId ? `post:${params.postId}` : params.outreachId ? `outreach:${params.outreachId}` : params.replyQueueId ? `reply_queue:${params.replyQueueId}` : params.readingId ? `reading:${params.readingId}` : params.campaignId ? `campaign_reading:${params.campaignId}` : params.factProductId ? `fact_review:${params.factProductId}` : params.ruleProductId ? `rule_review:${params.ruleProductId}` : params.dataProductId ? `data_setup:${params.dataProductId}` : params.noticeKey ? decodeURIComponent(params.noticeKey) : null;
  const inbox = useQuery(q.inbox);
  const products = useQuery(q.products);
  // Decisions first, then runs to start, then notices; J and K move in that order.
  const grouped = useMemo(() => groupInbox(inbox.data?.items ?? []), [inbox.data]);
  const items = grouped.flat;
  const [selected, setSelected] = useState<string | null>(openKey);
  const [intent, setIntent] = useState<Intent>(null);
  const rows = useRef(new Map<string, HTMLLIElement>());
  const title = useRef<HTMLHeadingElement>(null);
  const current = items.find((item) => item.key === selected) ?? items[0];
  const seen = useInboxSeen();
  const fresh = items.filter(seen.isNew).length;

  useEffect(() => { if (openKey) setSelected(openKey); }, [openKey]);
  // Opening a review is what makes an item no longer new.
  const opened = items.find((item) => item.key === openKey);
  const { markSeen } = seen;
  useEffect(() => { if (opened) markSeen(opened); }, [opened, markSeen]);
  // Time with a review open counts toward the person's attention (plan M5 step 5e).
  useAttention(opened?.product.id ?? null);
  const focusRow = useCallback((key: string | undefined) => requestAnimationFrame(() => { if (key && !narrow()) rows.current.get(key)?.focus(); }), []);
  const open = useCallback((item: C.InboxItem, next: Intent = null, replace = false) => {
    setIntent(next); setSelected(item.key);
    // A plan and a campaign's reading open as their own pages; A and X there start with the decision ready.
    void navigate({ to: hrefOf(item), search: (item.kind === "plan" || item.kind === "campaign_reading") && next ? { intent: next } : undefined, replace });
  }, [navigate]);
  const close = useCallback(() => { setIntent(null); void navigate({ to: "/inbox" }); focusRow(selected ?? undefined); }, [navigate, focusRow, selected]);
  const done = useCallback((message: string) => {
    const index = items.findIndex((item) => item.key === openKey);
    const rest = items.filter((item) => item.key !== openKey);
    const next = rest[Math.min(Math.max(index, 0), rest.length - 1)];
    client.setQueryData(q.inbox.queryKey, { items: rest });
    // The decision changed campaigns, actions and runs; refetch whatever is on screen.
    void client.invalidateQueries();
    if (next) { open(next, null, true); focusRow(next.key); }
    else { setSelected(null); setIntent(null); void navigate({ to: "/inbox", replace: true }); requestAnimationFrame(() => title.current?.focus()); }
    notify.success(message);
  }, [items, openKey, client, open, focusRow, navigate]);

  const move = (delta: number) => {
    if (!items.length) return;
    const index = Math.max(0, items.findIndex((item) => item.key === current?.key));
    const next = items[Math.min(items.length - 1, Math.max(0, index + delta))];
    setSelected(next.key);
    rows.current.get(next.key)?.focus();
    // With a review open, it follows the selection, as in Linear.
    if (openKey && next.key !== openKey) open(next, null, true);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
    if (key === "j" || key === "ArrowDown") move(1);
    else if (key === "k" || key === "ArrowUp") move(-1);
    else if (key === "Home") move(-items.length);
    else if (key === "End") move(items.length);
    else if (key === "Enter" && current) open(current);
    else if (key === "a" && decidable(current)) open(current!, "approve");
    else if (key === "x" && current?.kind === "plan") open(current, "reject");
    else if (key === "Escape" && openKey) close();
    else return;
    event.preventDefault();
  };

  const control = useMemo(() => ({ items, intent, close, done }), [items, intent, close, done]);
  if (inbox.isLoading) return <div className="z-inbox-page"><TopBar crumbs={[{ label: I.title }]} /><div className="z-inbox"><div className="z-inbox-list"><Skeleton /></div></div></div>;
  return <InboxContext.Provider value={control}>
    <div className="z-inbox-page">
      <TopBar crumbs={[{ label: I.title }]} actions={items.length > 0 && <InboxShortcuts item={current} opened={Boolean(openKey)} />} />
      <div className={`z-inbox${openKey ? " has-detail" : ""}`}>
        <div className="z-inbox-list">
          <header className="z-inbox-head"><h1 id="inbox-title" ref={title} tabIndex={-1}>{I.title}</h1>{items.length > 0 && <span className="z-count">{number(items.length)}</span>}{fresh > 0 && <span className="z-inbox-fresh">{I.newCount(fresh)}</span>}</header>
          <ErrorText error={inbox.error} />
          {inbox.data && !items.length && (products.data?.length === 0
            ? <Quiet title={I.emptyTitle}><p>{I.noProductsNote}</p><p><Link to="/new">{I.addProduct}</Link></p></Quiet>
            : <Quiet title={I.emptyTitle}><p>{I.emptyNote}</p></Quiet>)}
          {items.length > 0 && <ul className="z-rows z-boxed" role="listbox" aria-labelledby="inbox-title" onKeyDown={onKeyDown}>
            {grouped.groups.map(({ group, items: rows_ }) => <Fragment key={group}>
              <li role="presentation"><GroupHeader label={INBOX_GROUP_LABELS[group]} count={rows_.length} /></li>
              {rows_.map((item) => <ListRow key={item.key} id={`inbox-${item.key.replace(":", "-")}`} selected={item.key === current?.key} unread={seen.isNew(item) ? I.unread : ""}
                icon={<InboxItemIcon item={item} />} title={titleOf(item)} sub={I.kind[item.kind]}
                chips={<ProductLabel product={item.product} chip />} meta={metaOf(item)}
                executor={submitterOf(item)}
                onSelect={() => setSelected(item.key)} onOpen={() => open(item)}
                rowRef={(node) => { if (node) rows.current.set(item.key, node); else rows.current.delete(item.key); }} />)}
            </Fragment>)}
          </ul>}
        </div>
        <Outlet />
      </div>
    </div>
  </InboxContext.Provider>;
}
